import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, RefreshCcw, Mail, Phone, ExternalLink, Download, Send } from "lucide-react";
import { RevealLines } from "@/components/site/RevealText";
import AnalyticsPanel from "@/components/site/AnalyticsPanel";
import ContentPanel from "@/components/site/ContentPanel";
import CertificationsPanel from "@/components/site/CertificationsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "nexoriq_admin_token";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("submissions"); // 'submissions' | 'analytics' | 'partners'
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [statusSaving, setStatusSaving] = useState("");

  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, a, p] = await Promise.all([
        axios.get(`${API}/admin/rfq`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/analytics`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/partnership`, { headers: { "X-Admin-Token": token } }),
      ]);
      setRfqs(r.data);
      setStats(s.data);
      setAnalytics(a.data);
      setPartnerships(p.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        nav("/admin", { replace: true });
      } else {
        console.warn("admin: dashboard load failed", e);
        toast.error("Failed to load dashboard.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, nav]);

  useEffect(() => {
    if (!token) {
      nav("/admin", { replace: true });
      return;
    }
    load();
  }, [token, nav, load]);

  const logout = async () => {
    try {
      await axios.post(
        `${API}/admin/logout`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
    } catch (err) {
      console.warn("admin: server-side logout failed (token cleared locally)", err);
    }
    localStorage.removeItem(TOKEN_KEY);
    toast.success("Signed out.");
    nav("/admin", { replace: true });
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/rfq/export.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `nexoriq-rfqs-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rfqs.length} RFQ${rfqs.length === 1 ? "" : "s"}.`);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        nav("/admin", { replace: true });
      } else {
        toast.error("Export failed.");
      }
    } finally {
      setExporting(false);
    }
  };

  const [digesting, setDigesting] = useState(false);
  const sendDigest = async (period = "daily") => {
    setDigesting(true);
    try {
      const res = await axios.post(
        `${API}/admin/digest/send-now?period=${period}`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      if (res.data.sent) {
        toast.success(
          `${period === "weekly" ? "Weekly" : "Daily"} digest sent to ${res.data.to}.`,
        );
      } else {
        toast.error(
          `Digest built (${res.data.total_rfqs} RFQs) but the email dispatch failed. Check server logs.`,
        );
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        nav("/admin", { replace: true });
      } else {
        toast.error("Failed to send digest.");
      }
    } finally {
      setDigesting(false);
    }
  };

  const fmt = (d) => new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

  const updatePartnerStatus = async (pid, next) => {
    setStatusSaving(pid);
    try {
      const res = await axios.patch(
        `${API}/admin/partnership/${pid}`,
        { status: next },
        { headers: { "X-Admin-Token": token } },
      );
      setPartnerships((prev) => prev.map((p) => (p.id === pid ? { ...p, status: res.data.status } : p)));
      if (selectedPartner?.id === pid) {
        setSelectedPartner((sp) => (sp ? { ...sp, status: res.data.status } : sp));
      }
      toast.success(`Marked ${next}.`);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        nav("/admin", { replace: true });
      } else {
        toast.error("Failed to update status.");
      }
    } finally {
      setStatusSaving("");
    }
  };

  return (
    <div data-testid="page-admin-dashboard" className="min-h-[80vh]">
      <section className="px-6 md:px-10 pt-10 md:pt-16 pb-8 border-b border-nx-navy/20">
        <div className="flex items-center justify-between mb-6">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
            → Admin · Sourcing desk
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => sendDigest("daily")}
              disabled={digesting}
              className="nx-btn ghost !py-2 !px-3 !text-[0.62rem] disabled:opacity-60"
              data-testid="admin-send-digest"
              title="Email today's RFQ digest to source@nexoriq.co"
            >
              <Send size={12} /> {digesting ? "Sending…" : "Send digest"}
            </button>
            <button
              onClick={exportCsv}
              disabled={exporting || !rfqs.length}
              className="nx-btn ghost !py-2 !px-3 !text-[0.62rem] disabled:opacity-60"
              data-testid="admin-export-csv"
              title="Download all RFQs as CSV"
            >
              <Download size={12} /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
            <button
              onClick={load}
              className="nx-btn ghost !py-2 !px-3 !text-[0.62rem]"
              data-testid="admin-refresh"
            >
              <RefreshCcw size={12} /> Refresh
            </button>
            <button
              onClick={logout}
              className="nx-btn !py-2 !px-3 !text-[0.62rem]"
              data-testid="admin-logout"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>

        <RevealLines
          as="h1"
          lines={[
            <span key="1">
              {tab === "analytics"
                ? "Conversion."
                : tab === "partners"
                  ? "Partners."
                  : tab === "content"
                    ? "Content."
                    : tab === "certifications"
                      ? "Certifications."
                      : "Inbound briefs."}
            </span>,
          ]}
          className="font-cabinet font-black tracking-[-0.045em] text-nx-ink text-[12vw] md:text-[6.5vw] leading-[0.9]"
          mode="mount"
        />

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-1 border-b border-nx-navy/25" data-testid="admin-tabs">
          {[
            ["submissions", "Submissions"],
            ["partners", "Partners"],
            ["content", "Content"],
            ["certifications", "Certifications"],
            ["analytics", "Analytics"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={
                "px-4 py-2.5 -mb-px border-b-2 font-mono text-[0.68rem] uppercase tracking-[0.2em] transition-colors " +
                (tab === k
                  ? "border-nx-navy text-nx-navy"
                  : "border-transparent text-nx-steel2 hover:text-nx-navy")
              }
            >
              {label}
            </button>
          ))}
        </div>

        {/* Stats (only on submissions tab) */}
        {tab === "submissions" && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <Stat label="Total RFQs" value={stats?.total ?? "—"} testid="stat-total" />
            <Stat label="Last 7 days" value={stats?.last7 ?? "—"} testid="stat-week" />
            <Stat
              label="Top category"
              value={stats?.top_categories?.[0]?.category || "—"}
              small
              testid="stat-topcat"
            />
            <Stat
              label="Top count"
              value={stats?.top_categories?.[0]?.count ?? "—"}
              testid="stat-topcount"
            />
          </div>
        )}
      </section>

      {/* Analytics tab */}
      {tab === "analytics" && (
        <section className="px-6 md:px-10 py-12" data-testid="admin-analytics-tab">
          <AnalyticsPanel data={analytics} loading={loading} />
        </section>
      )}

      {/* Content tab */}
      {tab === "content" && (
        <section className="px-6 md:px-10 py-12" data-testid="admin-content-tab">
          <ContentPanel />
        </section>
      )}

      {/* Certifications tab */}
      {tab === "certifications" && (
        <section className="px-6 md:px-10 py-12" data-testid="admin-certifications-tab">
          <CertificationsPanel />
        </section>
      )}

      {/* Partners tab */}
      {tab === "partners" && (
        <section className="px-6 md:px-10 py-12" data-testid="admin-partners-tab">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy">
              Partner applications
            </h2>
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
              {partnerships.length} record{partnerships.length === 1 ? "" : "s"}
            </div>
          </div>

          {loading ? (
            <div className="font-mono text-sm uppercase tracking-[0.2em] text-nx-steel2" data-testid="admin-partners-loading">→ Loading…</div>
          ) : partnerships.length === 0 ? (
            <div className="border border-nx-navy p-6 text-nx-navy2" data-testid="admin-partners-empty">
              No partner applications yet. Submissions from /partners will appear here.
            </div>
          ) : (
            <div className="border border-nx-navy overflow-hidden" data-testid="admin-partners-table">
              <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-nx-navy text-nx-bg font-mono text-[0.62rem] uppercase tracking-[0.2em]">
                <div className="col-span-3">Company / Contact</div>
                <div className="col-span-3">Contact</div>
                <div className="col-span-3">Capabilities</div>
                <div className="col-span-1">Received</div>
                <div className="col-span-2">Status</div>
              </div>
              {partnerships.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 border-t border-nx-navy/15 items-start hover:bg-nx-bg2 transition-colors"
                  data-testid={`partner-row-${p.id}`}
                >
                  <div className="md:col-span-3">
                    <button
                      onClick={() => setSelectedPartner(p)}
                      className="text-left font-cabinet font-black text-nx-navy leading-tight hover:underline"
                      data-testid={`partner-view-${p.id}`}
                    >
                      {p.company}
                    </button>
                    <div className="text-xs text-nx-steel2 mt-0.5">{p.contact_name}</div>
                    {p.country && <div className="text-xs text-nx-steel2">{p.country}</div>}
                  </div>
                  <div className="md:col-span-3 space-y-1 text-sm">
                    <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-nx-navy hover:text-nx-navy2 nx-underline">
                      <Mail size={12} /> {p.email}
                    </a>
                    {p.phone && (
                      <a href={`tel:${p.phone}`} className="flex items-center gap-2 text-nx-navy2 text-xs">
                        <Phone size={11} /> {p.phone}
                      </a>
                    )}
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-nx-navy2 text-xs nx-underline break-all">
                        <ExternalLink size={11} /> {p.website.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </div>
                  <div className="md:col-span-3 text-sm text-nx-navy2 leading-snug line-clamp-3">{p.capabilities}</div>
                  <div className="md:col-span-1 text-xs text-nx-navy2 whitespace-nowrap">{fmt(p.created_at)}</div>
                  <div className="md:col-span-2">
                    <StatusPicker
                      value={p.status || "new"}
                      saving={statusSaving === p.id}
                      onChange={(next) => updatePartnerStatus(p.id, next)}
                      testid={`partner-status-${p.id}`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Submissions table (only when tab === submissions) */}
      {tab === "submissions" && (
      <section className="px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy">
            Submissions
          </h2>
          <div className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
            {rfqs.length} record{rfqs.length === 1 ? "" : "s"}
          </div>
        </div>

        {loading ? (
          <div
            className="font-mono text-sm uppercase tracking-[0.2em] text-nx-steel2"
            data-testid="admin-loading"
          >
            → Loading…
          </div>
        ) : rfqs.length === 0 ? (
          <div
            className="border border-nx-navy p-6 text-nx-navy2"
            data-testid="admin-empty"
          >
            No RFQs yet. Submissions from the public form will appear here.
          </div>
        ) : (
          <div className="border border-nx-navy overflow-hidden" data-testid="admin-table">
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-nx-navy text-nx-bg font-mono text-[0.62rem] uppercase tracking-[0.2em]">
              <div className="col-span-3">Name / Company</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-1">Received</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {rfqs.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.02 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 border-t border-nx-navy/15 items-start hover:bg-nx-bg2 transition-colors"
                data-testid={`rfq-row-${r.id}`}
              >
                <div className="md:col-span-3">
                  <div className="font-cabinet font-black text-nx-navy leading-tight">
                    {r.name}
                  </div>
                  {r.company && (
                    <div className="text-xs text-nx-steel2 mt-0.5">{r.company}</div>
                  )}
                  {r.country && (
                    <div className="text-xs text-nx-steel2">{r.country}</div>
                  )}
                </div>
                <div className="md:col-span-3 space-y-1 text-sm">
                  <a
                    href={`mailto:${r.email}`}
                    className="flex items-center gap-2 text-nx-navy hover:text-nx-navy2 nx-underline"
                  >
                    <Mail size={12} /> {r.email}
                  </a>
                  {r.phone && (
                    <a
                      href={`tel:${r.phone}`}
                      className="flex items-center gap-2 text-nx-navy2 text-xs"
                    >
                      <Phone size={11} /> {r.phone}
                    </a>
                  )}
                </div>
                <div className="md:col-span-2 text-sm">
                  <div className="font-medium text-nx-navy">{r.category}</div>
                  {r.quantity && (
                    <div className="text-xs text-nx-steel2 mt-0.5">Qty: {r.quantity}</div>
                  )}
                  {r.timeline && (
                    <div className="text-xs text-nx-steel2">{r.timeline}</div>
                  )}
                </div>
                <div className="md:col-span-2 text-sm" data-testid={`rfq-source-${r.id}`}>
                  <SourceBadge rfq={r} />
                </div>
                <div className="md:col-span-1 text-xs text-nx-navy2">
                  <div className="whitespace-nowrap">{fmt(r.created_at)}</div>
                  <div className="mt-1 inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.14em]">
                    <span
                      className={
                        "inline-block w-1.5 h-1.5 " +
                        (r.email_sent ? "bg-nx-navy" : "bg-nx-steel")
                      }
                    />
                    {r.email_sent ? "Emailed" : "Pending"}
                  </div>
                </div>
                <div className="md:col-span-1 md:text-right">
                  <button
                    onClick={() => setSelected(r)}
                    className="text-nx-navy border-b border-nx-navy pb-0.5 uppercase tracking-[0.14em] text-[0.65rem] hover:text-nx-navy2"
                    data-testid={`rfq-view-${r.id}`}
                  >
                    View brief
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-nx-navy/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4 md:p-10"
          onClick={() => setSelected(null)}
          data-testid="rfq-detail-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-nx-bg border border-nx-navy max-w-2xl w-full max-h-[85vh] overflow-y-auto"
          >
            <div className="px-6 py-4 bg-nx-navy text-nx-bg flex items-center justify-between">
              <div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-steel">
                  Ref {selected.id.slice(0, 8).toUpperCase()}
                </div>
                <div className="font-cabinet font-black text-xl mt-1">{selected.name}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-nx-bg text-2xl leading-none"
                aria-label="Close"
                data-testid="rfq-detail-close"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <DetailRow label="Email" value={<a href={`mailto:${selected.email}`} className="nx-underline">{selected.email}</a>} />
              <DetailRow label="Phone" value={selected.phone || "—"} />
              <DetailRow label="Company" value={selected.company || "—"} />
              <DetailRow label="Country" value={selected.country || "—"} />
              <DetailRow label="Category" value={selected.category} />
              <DetailRow label="Quantity" value={selected.quantity || "—"} />
              <DetailRow label="Timeline" value={selected.timeline || "—"} />
              <DetailRow label="Received" value={fmt(selected.created_at)} />
              <DetailRow
                label="Source"
                value={<SourceBadge rfq={selected} verbose />}
              />
              {selected.utm_campaign && (
                <DetailRow label="Campaign" value={selected.utm_campaign} />
              )}
              {selected.utm_medium && (
                <DetailRow label="Medium" value={selected.utm_medium} />
              )}
              {selected.referrer && (
                <DetailRow
                  label="Referrer"
                  value={
                    <a
                      href={selected.referrer}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nx-underline break-all"
                    >
                      {selected.referrer}
                    </a>
                  }
                />
              )}
              {selected.landing_path && (
                <DetailRow label="Landing" value={selected.landing_path} />
              )}
              <div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-steel2 mb-2">
                  Brief
                </div>
                <div className="whitespace-pre-wrap text-nx-navy leading-relaxed border-l-2 border-nx-navy pl-4">
                  {selected.message}
                </div>
              </div>
              <div className="pt-2 flex gap-3">
                <a
                  href={`mailto:${selected.email}?subject=Re: Nexoriq RFQ ${selected.id.slice(0, 8).toUpperCase()}`}
                  className="nx-btn"
                  data-testid="rfq-reply-email"
                >
                  Reply via email <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      {/* Partner detail modal */}
      {selectedPartner && (
        <div
          className="fixed inset-0 z-50 bg-nx-navy/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4 md:p-10"
          onClick={() => setSelectedPartner(null)}
          data-testid="partner-detail-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-nx-bg border border-nx-navy max-w-2xl w-full max-h-[85vh] overflow-y-auto"
          >
            <div className="px-6 py-4 bg-nx-navy text-nx-bg flex items-center justify-between">
              <div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-steel">
                  Partner · {selectedPartner.id.slice(0, 8).toUpperCase()}
                </div>
                <div className="font-cabinet font-black text-xl mt-1">{selectedPartner.company}</div>
              </div>
              <button
                onClick={() => setSelectedPartner(null)}
                className="text-nx-bg text-2xl leading-none"
                aria-label="Close"
                data-testid="partner-detail-close"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <DetailRow label="Contact" value={selectedPartner.contact_name} />
              <DetailRow label="Email" value={<a href={`mailto:${selectedPartner.email}`} className="nx-underline">{selectedPartner.email}</a>} />
              <DetailRow label="Phone" value={selectedPartner.phone || "—"} />
              <DetailRow label="Country" value={selectedPartner.country || "—"} />
              <DetailRow
                label="Website"
                value={selectedPartner.website ? (
                  <a href={selectedPartner.website} target="_blank" rel="noopener noreferrer" className="nx-underline break-all">
                    {selectedPartner.website}
                  </a>
                ) : "—"}
              />
              <DetailRow label="Years operating" value={selectedPartner.years_operating || "—"} />
              <DetailRow label="Capacity" value={selectedPartner.capacity || "—"} />
              <DetailRow label="Certifications" value={selectedPartner.certifications || "—"} />
              <DetailRow label="Capabilities" value={selectedPartner.capabilities} />
              <DetailRow label="Received" value={fmt(selectedPartner.created_at)} />
              <DetailRow
                label="Status"
                value={
                  <StatusPicker
                    value={selectedPartner.status || "new"}
                    saving={statusSaving === selectedPartner.id}
                    onChange={(next) => updatePartnerStatus(selectedPartner.id, next)}
                    testid={`partner-modal-status-${selectedPartner.id}`}
                  />
                }
              />
              <div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-steel2 mb-2">
                  Note from applicant
                </div>
                <div className="whitespace-pre-wrap text-nx-navy leading-relaxed border-l-2 border-nx-navy pl-4">
                  {selectedPartner.message}
                </div>
              </div>
              <div className="pt-2 flex gap-3">
                <a
                  href={`mailto:${selectedPartner.email}?subject=Nexoriq · Partner application ${selectedPartner.id.slice(0, 8).toUpperCase()}`}
                  className="nx-btn"
                  data-testid="partner-reply-email"
                >
                  Reply via email <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, small, testid }) {
  return (
    <div className="border-t border-nx-navy pt-3" data-testid={testid}>
      <div
        className={
          "font-cabinet font-black text-nx-navy tracking-[-0.03em] leading-none " +
          (small ? "text-xl md:text-2xl" : "text-3xl md:text-5xl")
        }
      >
        {value}
      </div>
      <div className="mt-2 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-nx-steel2">
        {label}
      </div>
    </div>
  );
}

const PARTNER_STATUSES = ["new", "reviewing", "approved", "declined"];
const STATUS_COLOR = {
  new: "border-nx-steel2 text-nx-steel2",
  reviewing: "border-nx-navy text-nx-navy",
  approved: "bg-nx-navy text-nx-bg border-nx-navy",
  declined: "border-nx-accent text-nx-accent",
};

function StatusPicker({ value, onChange, saving, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={
          "inline-flex items-center border px-2 py-0.5 font-mono text-[0.62rem] uppercase tracking-[0.14em] " +
          (STATUS_COLOR[value] || STATUS_COLOR.new)
        }
        data-testid={`${testid}-current`}
      >
        {value}
      </span>
      <select
        value={value}
        disabled={saving}
        onChange={(e) => e.target.value !== value && onChange(e.target.value)}
        className="bg-transparent border-b border-nx-navy/30 focus:border-nx-navy font-mono text-[0.6rem] uppercase tracking-[0.14em] text-nx-navy outline-none py-0.5 pr-1 disabled:opacity-50"
        data-testid={testid}
        aria-label="Change status"
      >
        {PARTNER_STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-nx-navy/10">
      <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-steel2 pt-1">
        {label}
      </div>
      <div className="col-span-2 text-sm text-nx-navy">{value}</div>
    </div>
  );
}


function SourceBadge({ rfq, verbose = false }) {
  const utm = rfq?.utm_source;
  const ref = rfq?.referrer;
  let label = "Direct";
  let host = null;
  if (utm) {
    label = utm;
  } else if (ref) {
    try {
      host = new URL(ref).hostname.replace(/^www\./, "");
      label = host;
    } catch {
      label = ref.slice(0, 40);
    }
  }
  const isPaid = rfq?.utm_medium && /paid|cpc|ppc|ads?/i.test(rfq.utm_medium);
  const tone = utm
    ? isPaid
      ? "bg-nx-navy text-nx-bg border-nx-navy"
      : "border-nx-navy text-nx-navy"
    : ref
      ? "border-nx-navy2 text-nx-navy2"
      : "border-nx-steel2 text-nx-steel2";
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={
          "inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[0.62rem] uppercase tracking-[0.14em] " +
          tone
        }
      >
        {label}
      </span>
      {verbose && rfq?.utm_medium && (
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-nx-steel2">
          {rfq.utm_medium}
          {rfq.utm_campaign ? ` · ${rfq.utm_campaign}` : ""}
        </span>
      )}
      {!verbose && rfq?.utm_campaign && (
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-nx-steel2 truncate max-w-full">
          {rfq.utm_campaign}
        </span>
      )}
    </div>
  );
}
